import { useRef } from "react";

import {
    BulkStrategyDepositParams,
    BulkStrategyWithdrawParams,
    CloseStrategyTxnParams,
    CreateCustomStrategyTxnParams,
    DepositStrategyTxnParams,
    EditCustomStrategyTxnParams,
    ExternalYieldSource,
    SellLoanParams,
    LenderBuildRequest,
    CreateLoanParams,
    WithdrawStrategyTxnParams,
    buildLenderTransactions,
    getCloseStrategyTransaction,
    getCreateLoanTransaction,
    getCreateStrategyTransaction,
    getDepositStrategyTransaction,
    getEditStrategyTransactions,
    getWithdrawStrategyTransction
} from "@bridgesplit/abf-sdk";
import { OrderedTransactions } from "@bridgesplit/react";
import { Result, uiAmountToLamports, AnalyticsTransactionType, StrategyCreateAnalyticsInfo } from "@bridgesplit/utils";
import { useSelector } from "react-redux";

import { getSlice, useAbfFetches } from "../reducers";
import { AbfGeneratorResult, AbfTransactionDetails, TransactionSenderOptions } from "../types";
import {
    buildBundledTransactionBatch,
    useAbfGenerateTransaction,
    useAbfGenerateTransactionWithSetup,
    wrapAsAbfGeneratorResult
} from "./common";
import { useCreateSimulationPromise } from "./simulation";
import { getTransactionHeadersFromCookies } from "../utils";
import { TRANSACTION_DEFAULT_BATCH_COMMITMENT } from "../constants";
import { useUserTokens } from "../api";

export type DepositStrategyTxnArgs = DepositStrategyTxnParams & {
    identifier?: string;
};

export function useDepositStrategyTransaction(): AbfTransactionDetails<DepositStrategyTxnArgs> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: DepositStrategyTxnArgs): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getDepositStrategyTransaction,
                identifier: params.identifier ?? "Deposit",
                params
            }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Depositing" };
}

export type WithdrawStrategyTxnArgs = WithdrawStrategyTxnParams & {
    identifier?: string;
};

export function useWithdrawStrategyTransaction(): AbfTransactionDetails<WithdrawStrategyTxnArgs> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: WithdrawStrategyTxnArgs): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getWithdrawStrategyTransction,
                identifier: params.identifier ?? "Withdraw",
                params
            }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Withdrawing" };
}

export function useUpdateStrategyTermsTransaction(): AbfTransactionDetails<EditCustomStrategyTxnParams> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: EditCustomStrategyTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getEditStrategyTransactions, identifier: "Update terms", params }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Updating terms" };
}

export function useCreateStrategyWithCollateralTransaction(): AbfTransactionDetails<CreateCustomStrategyTxnParams> {
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const generate = useAbfGenerateTransactionWithSetup();
    const state = useSelector(getSlice);
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParams({ ...params }: CreateCustomStrategyTxnParams): AbfGeneratorResult {
        try {
            const auth = getTransactionHeadersFromCookies({ authSlice: state });
            if (!auth.isOk()) return Result.err(auth);

            const analyticsInfo: StrategyCreateAnalyticsInfo = {
                transactionType: AnalyticsTransactionType.StrategyCreate,
                lender: params.lender,
                amount: params.amount,
                liquidityBuffer: params.liquidityBuffer,
                interestFee: params.interestFee,
                originationFee: params.originationFee,
                originationCap: params.originationCap,
                marketInformation: params.marketInformation,
                collateralTermCount: params.collateralTerms?.length,
                hasExternalYieldSource: !!params.externalYieldSourceArgs
            };

            const createStrategyWithCollateral = generate({
                generateFunction: getCreateStrategyTransaction,
                identifier: "Setup advanced lending position",
                params: params,
                analyticsInfo
            });

            const generatedTxns = await Promise.all([createStrategyWithCollateral]);
            const fail = generatedTxns.find((t) => !t?.isOk());
            if (fail) {
                return Result.err(fail);
            }

            const [setupTxn] = [generatedTxns[0].unwrap()];

            const txns: OrderedTransactions = [
                {
                    transactions: setupTxn.setupTransactions,
                    commitmentLevel: "confirmed"
                },
                { transactions: setupTxn.transactions, commitmentLevel: TRANSACTION_DEFAULT_BATCH_COMMITMENT }
            ];
            return Result.ok({ transactions: txns, simulationPromise: createSimulationPromise(txns, analyticsInfo) });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Creating custom strategy" };
}

export type CreateLoanTxnParams = Omit<CreateLoanParams, "mintToCollateralInfo"> & {
    loanTransactionIdentifier?: string; // optionally rename the transaction ID
};
export function useFillStrategyOfferTransaction(): AbfTransactionDetails<CreateLoanTxnParams> & {
    getLastLoanAddress: () => string | undefined;
} {
    const {
        resetOraclePricesApi,
        resetLoanApi,
        resetNapoleonApi,
        resetMarketsPublicApi,
        resetLendingVaultPositionsApi
    } = useAbfFetches();
    const { refresh } = useUserTokens();
    const state = useSelector(getSlice);
    const lastLoanAddressRef = useRef<string>();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParams({
        loanTransactionIdentifier,
        ...params
    }: CreateLoanTxnParams): AbfGeneratorResult {
        try {
            const auth = getTransactionHeadersFromCookies({ authSlice: state });
            if (!auth.isOk()) return Result.err(auth);

            const transactionsRes = await getCreateLoanTransaction(auth.unwrap(), params);

            if (!transactionsRes.isOk()) return Result.err(transactionsRes);

            const { transactions, loanAddress } = transactionsRes.unwrap();
            lastLoanAddressRef.current = loanAddress;

            const createLoanIdentifier = loanTransactionIdentifier ?? "Create loan";
            const finalTransactionIndex = transactions.length - 1;
            const orderedTransactions = buildBundledTransactionBatch(
                transactions.map((transaction, index) => ({
                    transaction,
                    identifier:
                        index === finalTransactionIndex
                            ? createLoanIdentifier
                            : formatOracleUpdateIdentifier(index, finalTransactionIndex)
                })),
                "confirmed"
            );

            return Result.ok({
                transactions: orderedTransactions,
                simulationPromise: createSimulationPromise(orderedTransactions)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        minGeyserConfirmations: 2,
        refetch: () => {
            resetLoanApi();
            resetNapoleonApi();
            resetMarketsPublicApi();
            resetLendingVaultPositionsApi();
            void refresh();
            resetOraclePricesApi();
        }
    };

    return {
        getTransactionsWithParams,
        sendOptions,
        description: "Filling borrow order",
        getLastLoanAddress: () => lastLoanAddressRef.current
    };
}

function formatOracleUpdateIdentifier(index: number, finalTransactionIndex: number): string {
    return finalTransactionIndex === 1 ? "Update oracle" : `Update oracle ${index + 1}`;
}

export type EditStrategySettingsParams = {
    strategyAddress: string;
    originationsEnabled: boolean;
    externalYieldSource: ExternalYieldSource | undefined;
};

export function useEditStrategySettings(): AbfTransactionDetails<EditStrategySettingsParams> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLoanApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({
        strategyAddress,
        originationsEnabled,
        externalYieldSource
    }: EditStrategySettingsParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getEditStrategyTransactions,
                identifier: "Update settings",
                params: {
                    strategy: strategyAddress,
                    updateParams: {
                        externalYieldSource: {
                            newExternalYieldSource: externalYieldSource ?? ExternalYieldSource.None
                        },
                        originationsEnabled
                    }
                }
            }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            resetLoanApi();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Updating loan liquidation preferences" };
}

export type SellLoanEntry = SellLoanParams & { decimals: number };
export interface SellLoanTransactionParams {
    entries: SellLoanEntry[];
}
export function useSellLoanTransaction(): AbfTransactionDetails<SellLoanTransactionParams> & {
    getTransactionsWithParamsAndSimulation: (params: SellLoanTransactionParams) => Promise<Result<OrderedTransactions>>;
} {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetLoanApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();

    const createSimulationPromise = useCreateSimulationPromise();

    function getTransactionsWithParams({ entries }: SellLoanTransactionParams): AbfGeneratorResult {
        const identifier = entries.length > 1 ? "Bulk early withdraw" : "Early withdraw";
        const params: LenderBuildRequest = {
            steps: entries.map(({ decimals, ...entry }) => [
                {
                    type: "sell",
                    params: {
                        ...entry,
                        sellParams: {
                            ...entry.sellParams,
                            expectedSalePrice: uiAmountToLamports(entry.sellParams.expectedSalePrice, decimals)
                        }
                    }
                }
            ])
        };
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: buildLenderTransactions, identifier, params }),
            createSimulationPromise,
            { order: "multiStep" }
        );
    }

    // Blocking simulation for dialogs that preview the sell before the user confirms.
    // Routes through simulationPromise so all simulation goes through simulateOrderedTransactions.
    async function getTransactionsWithParamsAndSimulation(
        params: SellLoanTransactionParams
    ): Promise<Result<OrderedTransactions>> {
        const result = await getTransactionsWithParams(params);
        if (result.isErr()) return Result.err(result);

        const { transactions, simulationPromise } = result.unwrap();
        const simResult = await simulationPromise;
        if (simResult?.error) return Result.errFromMessage(simResult.error, { skipSentry: true });

        return Result.ok(transactions);
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
            resetLoanApi();
        }
    };

    return {
        getTransactionsWithParams,
        getTransactionsWithParamsAndSimulation,
        sendOptions,
        description: "Selling loan"
    };
}

export interface BulkStrategyDepositTransactionParams {
    entries: BulkStrategyDepositParams[];
}

export function useBulkStrategyDepositTransaction(): AbfTransactionDetails<BulkStrategyDepositTransactionParams> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({ entries }: BulkStrategyDepositTransactionParams): AbfGeneratorResult {
        const identifier = entries.length > 1 ? "Bulk deposit" : "Deposit";
        const params: LenderBuildRequest = {
            steps: entries.map((entry) => [{ type: "strategyDeposit", params: entry }])
        };
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: buildLenderTransactions, identifier, params }),
            createSimulationPromise,
            { order: "multiStep" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Depositing" };
}

export interface BulkStrategyWithdrawTransactionParams {
    entries: BulkStrategyWithdrawParams[];
}

export function useBulkStrategyWithdrawTransaction(): AbfTransactionDetails<BulkStrategyWithdrawTransactionParams> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({ entries }: BulkStrategyWithdrawTransactionParams): AbfGeneratorResult {
        const identifier = entries.length > 1 ? "Bulk withdraw" : "Withdraw";
        const params: LenderBuildRequest = {
            steps: entries.map((entry) => [{ type: "strategyWithdraw", params: entry }])
        };
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: buildLenderTransactions, identifier, params }),
            createSimulationPromise,
            { order: "multiStep" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Withdrawing" };
}

export function useDeleteStrategyTransaction(): AbfTransactionDetails<CloseStrategyTxnParams> {
    const generate = useAbfGenerateTransaction();
    const { resetNapoleonApi, resetLendingStrategyApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: CloseStrategyTxnParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getCloseStrategyTransaction, identifier: "Close account", params }),
            createSimulationPromise,
            { order: "sequential" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
            resetLendingStrategyApi();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Closing accounts" };
}
